import { lapsedMoveBookings } from '@/domain/topupMoveLapse';
import { isCommittedBooking, isInsideCancelWindow, isLiveBooking, isWithAgent, studioDayKey, unansweredDmAfterPush } from '@atink/domain';
import { pickStudioAgent } from '@/domain/studioAgent';
import { listPendingOverrideRequests, requestAgeLabel } from '@/domain/approvals';
import { buildMissedLinks, type MissedLink } from '@/domain/missedLinks';
import { isConfirmationEscalated } from '@/domain/confirmationWorklist';
import { dayKey } from '@/utils/studioDay';
import type { SequenceState } from '@/domain/sequenceFlow';
import { customerName } from '@/utils/format';
import type {
  Agent,
  Artist,
  Booking,
  ConversationEntry,
  CustomerRecord,
  Deposit,
  NoRebookFlag,
  OverrideRequest,
  PaybyExtensionRequest,
  PayoutRecord,
  StudioId,
  TopupInstalment,
  TopupPlan,
} from '@/domain/types';

// ---------------------------------------------------------------------------
// Ops manager worklist (vault System Diagram 09). The accountability desk: every
// flagged item that falls out of the automated flows lands here for Ops (persona
// georgina) to action one at a time, worked to zero each day. Ops is the only
// role that can approve a no-deposit override or cancel a booking.
//
// The shell (tv-81c) fixed the section set, the section metadata, and the
// count-aggregation contract. tv-v4p replaces the empty buckets with the real
// per-feeder derivations below. The function stays PURE and deterministic
// (mirrors `buildConfirmationDay`): it reads only the read-models it is passed,
// never the store, and "now" is the sim clock the caller supplies.
// ---------------------------------------------------------------------------

/**
 * Worklist sections, in Ops-priority order: the two that need a human decision
 * first (overrides, confirmations), then the date/money items, then the
 * monitoring lists last.
 */
export const OPS_SECTION_KEYS = [
  'overrides',
  'confirmations',
  // W5 (approved tab order): pay-by extensions sit with the date/money decisions,
  // unclosed links after unpaid artists.
  'payby-extensions',
  'topup-extensions',
  'unpaid-artists',
  'unclosed-links',
  'no-rebook',
  'topups',
  'unanswered',
] as const;

export type OpsSectionKey = (typeof OPS_SECTION_KEYS)[number];

export interface OpsSectionMeta {
  key: OpsSectionKey;
  /** Tab label. */
  label: string;
  /** One-line description of what lands in this section. */
  blurb: string;
  /** The diagram-09 feeder this section drains (provenance + tv-v4p wiring). */
  feeder: string;
  /**
   * True when the section cannot be fully lit until an upstream dependency
   * lands. None are pending now: the no-rebook on-the-day flag (tv-3gl) has
   * landed in main, so every section drains a real feeder.
   */
  pending?: boolean;
}

export const OPS_SECTIONS: readonly OpsSectionMeta[] = [
  {
    key: 'overrides',
    label: 'Overrides',
    blurb: 'No-deposit override requests to approve or decline. Ops only.',
    feeder: 'i2 · no-deposit override request (Agent)',
  },
  {
    key: 'confirmations',
    label: 'Unconfirmed Appointments',
    blurb:
      'Bookings still unconfirmed after three sequences: ask the artist to try, or cancel.',
    feeder: 'i4 · confirmations 3rd-sequence flag',
  },
  {
    key: 'payby-extensions',
    label: 'Pay-by extensions',
    blurb:
      'Inside-7-day asks for more days to pay a deposit link, same appointment. Approve moves the pay-by; decline lets it lapse.',
    feeder: 'i5 · deposit / pay-by-date extension request',
  },
  {
    key: 'topup-extensions',
    label: 'Top-up extensions',
    blurb:
      'Inside-7-day top-up date-move approvals. Outside 7 days the Agent handles it directly.',
    feeder: 'i3 · inside-7d top-up extension approval (Agent)',
  },
  {
    key: 'unpaid-artists',
    label: 'Unpaid artists',
    blurb: 'Arrears to chase. Ops follows up the studio manager, not the artist.',
    feeder: 'i6 · unpaid artists',
  },
  {
    key: 'unclosed-links',
    label: 'Unclosed links',
    blurb:
      'Unpaid deposit links. Messenger links are yours to close; Agent and Sweeper links past 48h are shown for oversight only.',
    feeder: 'board 08 · link >48h unclosed (+ Messenger links > 24h)',
  },
  {
    key: 'no-rebook',
    label: 'No-rebook mid-piece',
    blurb:
      'Mid-piece sessions that ended with no rebook. Chase the unfinished work to a decision. Also checkouts where the artist didn’t walk the client down.',
    feeder: 'i7 · no-rebook mid-piece flag (On the day / Artist)',
  },
  {
    key: 'topups',
    label: 'Upcoming Top-ups',
    blurb: 'Upcoming top-ups by day of week, so a date an Agent moved gets noticed.',
    feeder: 'upTU · upcoming top-ups by day of week',
  },
  {
    key: 'unanswered',
    label: 'Unanswered messages',
    blurb: 'Instagram / Facebook DMs where the client spoke last, unanswered for more than 24 hours.',
    feeder: 'i1 · messages unanswered > 24h (Messenger)',
  },
];

// --- Per-section item shapes (discriminated on `kind`) ----------------------

/** i2 — a pending no-deposit override request awaiting an Ops decision. */
export interface OpsOverrideItem {
  id: string;
  kind: 'override';
  request: OverrideRequest;
  /** The held booking the request is against (absent if it has gone missing). */
  booking?: Booking;
  /** Short "how long ago" label ("3m", "2h", "1d"). */
  ageLabel: string;
}

/** i4 — a booking escalated after three unanswered confirmation sequences. */
export interface OpsConfirmationItem {
  id: string;
  kind: 'confirmation';
  booking: Booking;
  /** True once Ops has already asked the artist to personally reach out. */
  artistAsked: boolean;
  /** The response deadline set with the artist ask (vault 06 artist-tries path). */
  artistAskDeadlineIso?: string;
  /** 8xat: the artist ask's deadline has passed with no confirmation: Ops cancels. */
  deadlinePassed: boolean;
}

/** i3 — an inside-7-day top-up date move that needs an Ops approval. */
export interface OpsTopupExtensionItem {
  id: string;
  kind: 'topup-extension';
  plan: TopupPlan;
  booking?: Booking;
  /** Always 'inside' here — outside-7d moves are the Agent's job, never Ops's. */
  split: 'inside';
  /** The missed instalment's pay-by date under discussion. */
  payByIso?: string;
  /** D01-17: the date the client asked to move to, sent by the Agent (inside 7 days). */
  requestedStartIso?: string;
}

/**
 * B4 (75my; Anas 1 Oct Q4) — Ops is told when a top-up date move lapses: the
 * partial wasn't paid, so both dates were released and the money kept on file.
 * The Agent is calling back; this is Ops' awareness row (no action of its own).
 */
export interface OpsMoveLapsedItem {
  id: string;
  kind: 'move-lapsed';
  /** The released OLD booking. */
  booking: Booking;
}

/** i6 — one work day an artist is still owed (a written deferral / arrears). */
export interface OpsUnpaidArtistItem {
  id: string;
  kind: 'unpaid-artist';
  studioId: StudioId;
  artistId: string;
  artistName: string;
  amountAud: number;
  /** The work day the envelope is for. */
  dayIso: string;
}

/** i7 — a mid-piece session that ended with no rebook, flagged to Ops. */
export interface OpsNoRebookItem {
  id: string;
  kind: 'no-rebook';
  flag: NoRebookFlag;
}

/** True while a checkout's "did NOT come down" is an open accountability issue (D10-14). */
export function isOpenCameDownIssue(b: Pick<Booking, 'cameDown' | 'cameDownResolvedAt'>): boolean {
  return b.cameDown === 'did-not-come-down' && !b.cameDownResolvedAt;
}

/**
 * L5 (tv-hrsz, D10-14) — a checkout where the artist did NOT walk the client
 * down: an accountability issue until Ops resolves it. Derived from server state
 * (the booking's `cameDown` and its resolved marker); rides in the No-rebook tab
 * (the on-the-day accountability list), the way `move-lapsed` rides in Top-up
 * extensions.
 */
export interface OpsCameDownItem {
  /** `came-down-<bookingId>`. */
  id: string;
  kind: 'came-down';
  booking: Booking;
  /** The artist's display name (never the raw id). */
  artistName: string;
}

/** upTU — an upcoming top-up instalment, for the by-day-of-week overview. */
export interface OpsTopupItem {
  id: string;
  kind: 'topup';
  plan: TopupPlan;
  instalment: TopupInstalment;
  dueIso: string;
  /** Studio-local day of week (0 = Sun .. 6 = Sat) of the due date. */
  dayOfWeek: number;
}

/** i1 — a customer whose last inbound message is older than 24h, unanswered. */
export interface OpsUnansweredItem {
  id: string;
  kind: 'unanswered';
  customerId: string;
  customerName: string;
  studioId: StudioId;
  lastInbound: ConversationEntry;
  /**
   * Anas 8 Oct: the lead is the Agent's (pushed), and its "Client messaged"
   * alert has gone unanswered for over 24h. Absent = a Messenger thread.
   */
  withAgent?: boolean;
  /** The studio's bookings agent, when one covers it (the person whose reply is overdue). */
  agentName?: string;
}

/**
 * W5 mg — an unpaid deposit link on the Ops desk. `readOnly` = Agent / Sweeper
 * owned (Ops oversight only); a Messenger link is Ops's to action.
 */
export interface OpsUnclosedLinkItem {
  /** `missed-link-<depositId>`. */
  id: string;
  kind: 'unclosed-link';
  link: MissedLink;
  readOnly: boolean;
}

/** W5 i5 — an inside-7-day pay-by extension request awaiting an Ops decision. */
export interface OpsPaybyExtensionItem {
  id: string;
  kind: 'payby-extension';
  request: PaybyExtensionRequest;
  /** The unpaid link being extended (absent if it has gone missing). */
  deposit?: Deposit;
  /** The held appointment (unchanged by the extension). */
  booking?: Booking;
  /** Short "how long ago" label for the ask. */
  ageLabel: string;
}

export type OpsItem =
  | OpsOverrideItem
  | OpsConfirmationItem
  | OpsPaybyExtensionItem
  | OpsUnclosedLinkItem
  | OpsTopupExtensionItem
  | OpsMoveLapsedItem
  | OpsUnpaidArtistItem
  | OpsNoRebookItem
  | OpsCameDownItem
  | OpsTopupItem
  | OpsUnansweredItem;

export type OpsSectionBuckets = Record<OpsSectionKey, OpsItem[]>;
export type OpsSectionCounts = Record<OpsSectionKey, number> & { total: number };

export interface OpsWorklist {
  sections: OpsSectionBuckets;
  counts: OpsSectionCounts;
}

/**
 * Only the fields the unpaid-artists derivation reads. A full `Record<string,
 * Artist>` from the store is assignable, so the screen passes its store map
 * straight in while tests can supply just a display name.
 */
export type OpsArtistLite = Pick<Artist, 'id' | 'displayName'>;

/** Only the customer fields the unanswered derivation reads (store map assignable). */
export type OpsCustomerLite = Pick<
  CustomerRecord,
  'id' | 'firstName' | 'lastName' | 'preferredName' | 'primaryStudioId' | 'conversation' | 'pushedToAgentAt'
>;

/** The agent fields the Ops worklist reads to name a studio's bookings agent. */
export type OpsAgentLite = Pick<Agent, 'id' | 'firstName' | 'lastName' | 'role' | 'primaryStudio' | 'assignedStudios'>;

/**
 * D01-17: the date a client asked to move to, while that request still stands:
 * the plan is active, its booking is live, still on the start the request was
 * made against (a request goes stale once the appointment moves), and the
 * appointment's studio day hasn't passed. Undefined otherwise. Shared by the
 * Ops worklist and the Agent's move panel so both agree.
 */
export function liveMoveRequest(
  plan: Pick<TopupPlan, 'status' | 'moveRequestedStartIso' | 'moveRequestedFromIso'>,
  booking: Pick<Booking, 'start' | 'status'> | undefined,
  nowIso: string,
): string | undefined {
  if (!plan.moveRequestedStartIso || plan.status !== 'active' || !booking || !isLiveBooking(booking)) return undefined;
  if (plan.moveRequestedFromIso && Date.parse(plan.moveRequestedFromIso) !== Date.parse(booking.start)) return undefined;
  if (studioDayKey(booking.start) < studioDayKey(nowIso)) return undefined;
  return plan.moveRequestedStartIso;
}

/**
 * B8 (N8) + tv-3zjo + enquiries-9: the customers whose lead is with the Agent,
 * by the ONE shared rule `isWithAgent` (@atink/domain): a pushed sequence
 * record (red included, Anas 8 Oct), or — once a booking/cancel/lapse deleted
 * the record — the durable server-stamped marker. The store-side mirror of
 * the opsView's `pushedCustomerIds`.
 */
export function pushedCustomerIdsOf(
  sequenceState: Record<string, Pick<SequenceState, 'stage' | 'sourceConversationId'> | undefined>,
  customers: Record<string, Pick<CustomerRecord, 'id' | 'pushedToAgentAt'> | undefined>,
): string[] {
  const ids = new Set<string>([...Object.keys(sequenceState), ...Object.keys(customers)]);
  return [...ids].filter((id) => isWithAgent(customers[id], sequenceState[id]));
}

/**
 * tv-3zjo: customers with a booked session still to come or under way
 * (committed — a held link doesn't count — and not finished). Their DM thread
 * is the booked client's, not a Messenger lead, so it never raises Unanswered.
 */
export type OpsCustomerBookingLite = Pick<Booking, 'customerId' | 'status' | 'start' | 'durationMin' | 'overbookable'>;

function customersWithUpcomingBooking(bookings: Iterable<OpsCustomerBookingLite>, nowMs: number): Set<string> {
  const ids = new Set<string>();
  for (const b of bookings) {
    if (!isCommittedBooking(b) || b.status === 'completed') continue;
    const endMs = new Date(b.start).getTime() + (b.durationMin ?? 0) * 60_000;
    if (endMs >= nowMs) ids.add(b.customerId);
  }
  return ids;
}

export interface OpsImmediateUnsignedLite {
  studioId: StudioId;
  artistId: string;
  artistName: string;
  amountAud: number;
  dayIso: string;
}

export interface BuildOpsWorklistArgs {
  /** The workday to build the list for; "now" per the sim clock. */
  nowIso: string;
  /** Studio scope; [] = all studios. Every feeder filters to these. */
  studioIds: StudioId[];
  /** Read-models the feeders drain. Absent === none of that kind, so the desk is empty. */
  bookings?: Record<string, Booking>;
  overrideRequests?: Record<string, OverrideRequest>;
  topupPlans?: Record<string, TopupPlan>;
  noRebookFlags?: Record<string, NoRebookFlag>;
  payoutRecords?: Record<string, PayoutRecord>;
  /**
   * Payable-but-unsigned payouts for the current day — surfaced IMMEDIATELY on
   * the Ops desk, not only as day-close arrears (Diagram 10 / tv-gil). The caller
   * computes these (store-reading) and passes them so this stays pure.
   */
  immediateUnsigned?: OpsImmediateUnsignedLite[];
  artists?: Record<string, OpsArtistLite>;
  customers?: Record<string, OpsCustomerLite>;
  /** W5 mg: deposits carrying the unpaid links (opsView `deposits`). */
  deposits?: Record<string, Deposit>;
  /** W5 i5: pay-by extension requests (opsView `paybyExtensionRequests`). */
  paybyExtensionRequests?: Record<string, PaybyExtensionRequest>;
  /**
   * B8 (N8, diagram 03): customers the Messenger pushed to the Agent. A push is a
   * close ("hand to Agent"), so their DM thread never raises Unanswered > 24h.
   */
  pushedCustomerIds?: Iterable<string>;
  /**
   * tv-3zjo: the Unanswered customers' own bookings at ANY studio (the scoped
   * `bookings` are filed by the artist's studio, so a booking elsewhere would be
   * missed). Read only to skip a client with a booked session coming up.
   */
  customerBookings?: Iterable<OpsCustomerBookingLite>;
  /**
   * W5 mh: the unpaid-artist rows precomputed by `opsUnpaidArtists` (built from
   * the ops-view data). When passed it IS the section; otherwise the legacy
   * immediate-unsigned + deferred-record derivation below runs.
   */
  unpaidArtists?: OpsUnpaidArtistItem[];
  /** Anas 8 Oct: the agents, to name the bookings agent whose "Client messaged" is overdue. */
  agents?: Record<string, OpsAgentLite>;
}

/** Meta DMs (Instagram / Facebook) — the only channel the unanswered rule reads. */
export const META_CHANNELS: ReadonlySet<ConversationEntry['channel']> = new Set(['meta-dm', 'ig-dm']);

/**
 * W5 mf: the customer's LAST Meta entry, when it is inbound (the client spoke
 * last on Instagram / Facebook). A call, voicemail or SMS never opens a row, and
 * only a Meta reply clears one.
 */
export function lastUnansweredMeta(conversation: ConversationEntry[]): ConversationEntry | undefined {
  for (let i = conversation.length - 1; i >= 0; i -= 1) {
    const e = conversation[i]!;
    if (!META_CHANNELS.has(e.channel)) continue;
    return e.direction === 'in' ? e : undefined;
  }
  return undefined;
}

function countBuckets(sections: OpsSectionBuckets): OpsSectionCounts {
  const perSection = Object.fromEntries(
    OPS_SECTION_KEYS.map((key) => [key, sections[key].length]),
  ) as Record<OpsSectionKey, number>;
  const total = OPS_SECTION_KEYS.reduce((sum, key) => sum + sections[key].length, 0);
  return { ...perSection, total };
}

const DAY_MS = 24 * 3_600_000;

/**
 * Build the Ops worklist for a day + studio scope from the passed-in read-models.
 * Pure + deterministic: no store reads, no `Date.now()`. Each feeder maps onto its
 * section per the runbook's diagram-09 feeder->signal map.
 */
export function buildOpsWorklist(args: BuildOpsWorklistArgs): OpsWorklist {
  const {
    nowIso,
    studioIds,
    bookings = {},
    overrideRequests = {},
    topupPlans = {},
    noRebookFlags = {},
    payoutRecords = {},
    immediateUnsigned = [],
    artists = {},
    customers = {},
    deposits = {},
    paybyExtensionRequests = {},
  } = args;
  const nowMs = new Date(nowIso).getTime();
  const inScope = (studioId: StudioId): boolean =>
    studioIds.length === 0 || studioIds.includes(studioId);

  // i2 · overrides — pending no-deposit requests, oldest first, with their booking.
  // An OverrideRequest carries no studioId of its own; the studio lives on the
  // referenced booking. When that booking is absent from the read-model (the seed
  // evicts the held Coburg demo-day approval bookings while leaving their pending
  // requests intact) the studio is unknowable, so we CANNOT prove the request is
  // out of scope. Surface it anyway rather than silently dropping a real pending
  // decision — this matches /approvals, which lists every pending request. A
  // present booking is still scoped as before.
  const overrides: OpsItem[] = listPendingOverrideRequests(overrideRequests)
    .map((request) => ({ request, booking: bookings[request.bookingId] }))
    .filter(
      ({ booking }) =>
        studioIds.length === 0 || !booking || studioIds.includes(booking.studioId),
    )
    .map(
      ({ request, booking }): OpsOverrideItem => ({
        id: request.id,
        kind: 'override',
        request,
        booking,
        ageLabel: requestAgeLabel(request.createdAt, nowMs),
      }),
    );

  // i4 · confirmations — escalated (3 unanswered sequences, still unconfirmed).
  const confirmations: OpsItem[] = Object.values(bookings)
    // Once cancelled or superseded the row goes (W5 m9) — the escalation stamps stay on the dead record.
    .filter((b) => isLiveBooking(b) && isConfirmationEscalated(b) && inScope(b.studioId))
    .map(
      (booking): OpsConfirmationItem => ({
        id: booking.id,
        kind: 'confirmation',
        booking,
        artistAsked: !!booking.artistAskedAt,
        ...(booking.artistAskDeadlineIso ? { artistAskDeadlineIso: booking.artistAskDeadlineIso } : {}),
        deadlinePassed:
          !!booking.artistAskedAt &&
          !!booking.artistAskDeadlineIso &&
          new Date(booking.artistAskDeadlineIso).getTime() < new Date(nowIso).getTime(),
      }),
    )
    // A lapsed artist deadline is the one to act on now (8xat): those rows first,
    // then by appointment.
    .sort((a, b) => Number(b.deadlinePassed) - Number(a.deadlinePassed) || a.booking.start.localeCompare(b.booking.start));

  // i3 · topup-extensions — active plans INSIDE 7 days of their appointment
  // (studio days, D01-17) that need Ops: a missed instalment, or a move the
  // Agent sent over with the date the client asked for (D01-17). Outside 7 days
  // is the Agent's job and never appears here.
  const topupExtensions: OpsItem[] = Object.values(topupPlans)
    .filter((p) => p.status === 'active' && inScope(p.studioId))
    .map((plan) => {
      const booking = plan.bookingId ? bookings[plan.bookingId] : undefined;
      return { plan, booking, requested: liveMoveRequest(plan, booking, nowIso) };
    })
    .filter((x) => x.plan.instalments.some((i) => i.status === 'missed') || !!x.requested)
    .filter(
      (x): x is { plan: TopupPlan; booking: Booking; requested: string | undefined } =>
        x.booking !== undefined && isInsideCancelWindow(x.booking.start, nowIso),
    )
    .sort((a, b) => a.booking.start.localeCompare(b.booking.start))
    .map(
      ({ plan, booking, requested }): OpsTopupExtensionItem => ({
        id: plan.id,
        kind: 'topup-extension',
        plan,
        booking,
        split: 'inside',
        payByIso: plan.instalments.find((i) => i.status === 'missed')?.dueIso,
        ...(requested ? { requestedStartIso: requested } : {}),
      }),
    );

  // i6 · unpaid-artists — payable-but-unsigned payouts surfaced IMMEDIATELY
  // (tv-gil) PLUS written arrears (deferred payout records). Immediates that have
  // already become a deferred record are dropped so the artist shows once.
  const deferredKeys = new Set(
    Object.values(payoutRecords)
      .filter((r) => r.status === 'deferred')
      .map((r) => `${r.artistId}:${r.dayIso}`),
  );
  const immediateItems: OpsUnpaidArtistItem[] = immediateUnsigned
    .filter((u) => inScope(u.studioId) && !deferredKeys.has(`${u.artistId}:${u.dayIso}`))
    .map((u): OpsUnpaidArtistItem => ({
      id: `unsigned-${u.studioId}-${u.artistId}-${u.dayIso}`,
      kind: 'unpaid-artist',
      studioId: u.studioId,
      artistId: u.artistId,
      artistName: u.artistName,
      amountAud: u.amountAud,
      dayIso: u.dayIso,
    }));
  const deferredItems: OpsUnpaidArtistItem[] = Object.values(payoutRecords)
    .filter((r) => r.status === 'deferred' && inScope(r.studioId))
    .sort((a, b) => a.dayIso.localeCompare(b.dayIso))
    .map((r): OpsUnpaidArtistItem => ({
      id: r.id,
      kind: 'unpaid-artist',
      studioId: r.studioId,
      artistId: r.artistId,
      artistName: artists[r.artistId]?.displayName ?? r.artistId,
      amountAud: r.amountDueAud,
      dayIso: r.dayIso,
    }));
  const unpaidArtists: OpsItem[] = args.unpaidArtists
    ? args.unpaidArtists.filter((u) => inScope(u.studioId))
    : [...immediateItems, ...deferredItems];

  // i5 · payby-extensions — pending inside-7-day asks, oldest first (W5).
  const paybyExtensions: OpsItem[] = Object.values(paybyExtensionRequests)
    .filter((r) => r.status === 'pending' && inScope(r.studioId))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map(
      (request): OpsPaybyExtensionItem => ({
        id: request.id,
        kind: 'payby-extension',
        request,
        deposit: deposits[request.depositId],
        booking: request.bookingId ? bookings[request.bookingId] : undefined,
        ageLabel: requestAgeLabel(request.createdAt, nowMs),
      }),
    );

  // board 08 · unclosed-links — unpaid links Ops sees: Messenger-sent (actionable)
  // past 24h, Agent / Sweeper-sent (read-only) past 48h. Most overdue first (W5).
  const unclosedLinks: OpsItem[] = buildMissedLinks({ nowIso, deposits, bookings, studioIds, topupPlans })
    .filter((l) => l.opsVisible)
    .map((link): OpsUnclosedLinkItem => ({ id: link.id, kind: 'unclosed-link', link, readOnly: link.opsReadOnly }));

  // i7 · no-rebook — unresolved mid-piece flags (tv-3gl landed; now a live feeder).
  const noRebook: OpsItem[] = Object.values(noRebookFlags)
    .filter((f) => !f.resolvedAt && inScope(f.studioId))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map((flag): OpsNoRebookItem => ({ id: flag.id, kind: 'no-rebook', flag }));

  // upTU · topups — the forward overview: each active plan's next FUTURE scheduled
  // instalment, so a date an Agent moved gets noticed under Ops.
  const topups: OpsItem[] = Object.values(topupPlans)
    .filter((p) => p.status === 'active' && inScope(p.studioId))
    .map((plan) => {
      const next = plan.instalments
        .filter((i) => i.status === 'scheduled' && new Date(i.dueIso).getTime() > nowMs)
        .sort((a, b) => a.dueIso.localeCompare(b.dueIso))[0];
      return next ? { plan, next } : undefined;
    })
    .filter((x): x is { plan: TopupPlan; next: TopupInstalment } => x !== undefined)
    .sort((a, b) => a.next.dueIso.localeCompare(b.next.dueIso))
    .map(
      ({ plan, next }): OpsTopupItem => ({
        id: plan.id,
        kind: 'topup',
        plan,
        instalment: next,
        dueIso: next.dueIso,
        dayOfWeek: new Date(`${dayKey(next.dueIso)}T12:00:00+10:00`).getDay(),
      }),
    );

  // i1 · unanswered — customers whose LAST Meta (IG / FB) entry is inbound and
  // older than 24h on the sim clock (W5 mf). Other channels never count.
  // B8 (N8) + tv-3zjo: never a lead pushed to the Agent (record or durable
  // marker), nor a client with a booked session coming up.
  // enquiries-9: the caller's list carries the shared isWithAgent rule (sequence
  // row + durable marker); without one, the durable marker alone decides.
  const pushed = args.pushedCustomerIds ? new Set(args.pushedCustomerIds) : null;
  const withAgent = (c: OpsCustomerLite) =>
    pushed ? pushed.has(c.id) : isWithAgent(c, undefined);
  const booked = customersWithUpcomingBooking([...Object.values(bookings), ...(args.customerBookings ?? [])], nowMs);
  const overdue = (e: ConversationEntry | undefined): e is ConversationEntry =>
    e !== undefined && nowMs - new Date(e.timestamp).getTime() > DAY_MS;
  const messengerRows = Object.values(customers)
    .filter((c) => inScope(c.primaryStudioId) && !withAgent(c) && !booked.has(c.id))
    .map((c) => ({ c, last: lastUnansweredMeta(c.conversation), agent: false }));
  // Anas 8 Oct: a pushed lead stays the Agent's for good (after a booking or a
  // red sequence too), so the Messenger rule above never sees it. When the
  // Agent leaves its "Client messaged" alert unanswered for 24h, Ops sees it
  // here, with the studio's bookings agent named. The booked-session exception
  // doesn't apply: the Agent owns the booked client's DMs as well.
  const agentRows = Object.values(customers)
    .filter((c) => inScope(c.primaryStudioId) && withAgent(c))
    .map((c) => ({ c, last: unansweredDmAfterPush(c, c.conversation), agent: true }));
  const agentList = Object.values(args.agents ?? {});
  const unanswered: OpsItem[] = [...messengerRows, ...agentRows]
    .filter((x): x is { c: OpsCustomerLite; last: ConversationEntry; agent: boolean } => overdue(x.last))
    .sort((a, b) => a.last.timestamp.localeCompare(b.last.timestamp))
    .map(({ c, last, agent }): OpsUnansweredItem => {
      const named = agent ? pickStudioAgent(agentList, c.primaryStudioId) : undefined;
      return {
        id: c.id,
        kind: 'unanswered',
        customerId: c.id,
        customerName: customerName(c),
        studioId: c.primaryStudioId,
        lastInbound: last,
        ...(agent ? { withAgent: true } : {}),
        ...(named ? { agentName: `${named.firstName} ${named.lastName}`.trim() } : {}),
      };
    });

  const sections: OpsSectionBuckets = {
    overrides,
    confirmations,
    'payby-extensions': paybyExtensions,
    'topup-extensions': [
      ...topupExtensions,
      ...lapsedMoveBookings(bookings, nowIso)
        .filter((b) => inScope(b.studioId))
        .map((booking): OpsMoveLapsedItem => ({ id: `move-lapsed-${booking.id}`, kind: 'move-lapsed', booking })),
    ],
    'unpaid-artists': unpaidArtists,
    'unclosed-links': unclosedLinks,
    'no-rebook': [
      ...noRebook,
      ...Object.values(bookings)
        .filter((b) => b.status === 'completed' && isOpenCameDownIssue(b) && inScope(b.studioId))
        .sort((a, b) => (a.checkedOutAt ?? a.start).localeCompare(b.checkedOutAt ?? b.start))
        .map(
          (booking): OpsCameDownItem => ({
            id: `came-down-${booking.id}`,
            kind: 'came-down',
            booking,
            // The artist's NAME, never the raw id (D10-14).
            artistName: artists[booking.artistId]?.displayName ?? 'The artist',
          }),
        ),
    ],
    topups,
    unanswered,
  };
  return { sections, counts: countBuckets(sections) };
}
